import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GrowthPanel } from "./GrowthPanel";
import client, { imgUrl, logoUrl, assetUrl } from "@/lib/glossyApi";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LayoutGrid, Users, Newspaper, Share2, Sparkles, Image as ImageIcon,
  Check, Trash2, RefreshCw, Send, X, LogOut, Plus, Pencil, Upload, Save, Eye, Megaphone, Star, PenLine, Zap, Gift, ArrowLeft, Tag, ExternalLink, Table2, Archive as ArchiveIcon, Clock, ChevronUp, ChevronDown, FileText, Globe,
} from "lucide-react";
import { LONDON_AREAS } from "@/lib/londonAreas";
import ShareKit from "@/components/ShareKit";
import WebsiteImport, { mergeProfile } from "@/components/WebsiteImport";
import { cycleIndexFor, editionLabel, editionStartIso, issueNoFor, upcomingEditions } from "@/lib/issue";
import { AD_SLOT_META, CATEGORIES, SECTIONS, TONES, LENGTHS, MCATS, BUSINESS_CATS, Header, Stat, InboxGroup, InboxRow, DIn, DArea, Badge, Panel, Drawer, Field, Area, Select, ComboField } from "./shared";

function HealthScanPanel() {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showMinor, setShowMinor] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const r = await client.get("/admin/health-scan"); setRes(r.data); }
    catch { toast.error("Scan failed — try again."); }
    setBusy(false);
  };
  const shown = (res?.issues || []).filter((i) => showMinor || !i.minor);
  return (
    <div className="mx-6 sm:mx-10 mt-6 border border-ink/15 p-6 sm:p-7" data-testid="health-scan-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="overline text-gloss-silver">Magazine health scan</p>
          <p className="font-sans text-sm text-ink/55 mt-1">Sweeps every member, article and notice for mistakes — placeholder text, broken addresses, missing contacts. Free, no studio spend.</p>
        </div>
        <button onClick={run} disabled={busy} className="overline text-[11px] px-4 py-2.5 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50" data-testid="health-scan-run">
          {busy ? "Scanning…" : res ? "Scan again" : "Run health scan (free)"}
        </button>
      </div>
      {res && (
        <div className="mt-5">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`overline text-[10px] px-2.5 py-1 ${res.counts.warn === 0 ? "bg-green-700 text-cream" : "bg-amber-500 text-ink"}`} data-testid="health-scan-verdict">
              {res.counts.warn === 0 ? "All clean — nothing needs you" : `${res.counts.warn} thing${res.counts.warn === 1 ? "" : "s"} need${res.counts.warn === 1 ? "s" : ""} attention`}
            </span>
            {res.counts.minor > 0 && (
              <button onClick={() => setShowMinor(!showMinor)} className="font-mono text-[10px] text-ink/50 underline underline-offset-2" data-testid="health-scan-minor-toggle">
                {showMinor ? "Hide" : "Show"} {res.counts.minor} minor note{res.counts.minor === 1 ? "" : "s"}
              </button>
            )}
          </div>
          {shown.length > 0 && (
            <ul className="mt-3 space-y-1.5 max-h-72 overflow-y-auto pr-2">
              {shown.map((i, idx) => (
                <li key={idx} className="font-sans text-xs flex gap-2 items-baseline">
                  <span className={`shrink-0 overline text-[9px] px-1.5 py-0.5 ${i.minor ? "bg-ink/10 text-ink/50" : "bg-amber-500/20 text-amber-700"}`}>{i.where}</span>
                  <span className="font-medium shrink-0">{i.who}</span>
                  <span className="text-ink/65">— {i.note}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function StripeHealthPanel() {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const r = await client.get("/admin/billing/stripe-health"); setRes(r.data); }
    catch (e) { setRes({ ok: false, problem: e?.response?.data?.detail || "Could not reach the payment system." }); }
    setBusy(false);
  };
  useEffect(() => { run(); }, []);
  return (
    <div className="mx-6 sm:mx-10 mt-6 border border-ink/15 p-6 sm:p-7" data-testid="stripe-health-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="overline text-gloss-silver">Payments health</p>
          <p className="font-sans text-sm text-ink/55 mt-1">A live check on your Stripe account — key, account status and membership prices. Free, checked every time you open this page.</p>
        </div>
        <button onClick={run} disabled={busy} className="overline text-[11px] px-4 py-2.5 border border-ink/30 hover:bg-ink hover:text-cream transition-colors disabled:opacity-50" data-testid="stripe-health-refresh">
          {busy ? "Checking…" : "Check again"}
        </button>
      </div>
      {!res && busy && <p className="font-sans text-sm text-ink/50 mt-4">Asking Stripe…</p>}
      {res && (
        <div className="mt-4">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`overline text-[10px] px-2.5 py-1 ${res.ok ? "bg-green-700 text-cream" : "bg-red-600 text-cream"}`} data-testid="stripe-health-status">
              {res.ok ? "Payments healthy — members can pay" : "Payments problem — act now"}
            </span>
            {res.key_mode && (
              <span className="font-mono text-[10px] text-ink/50" data-testid="stripe-health-mode">
                {res.key_mode === "live" ? "Live key" : "Test key"}{res.account_id ? ` · ${res.account_id}` : ""}
                {typeof res.membership_prices_found === "number" ? ` · ${res.membership_prices_found} membership price${res.membership_prices_found === 1 ? "" : "s"}` : ""}
                {res.charges_enabled === false && res.key_mode === "live" ? " · charges paused" : ""}
              </span>
            )}
          </div>
          {!res.ok && res.problem && (
            <p className="font-sans text-xs text-red-700 mt-2" data-testid="stripe-health-problem">{res.problem}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function Overview({ onOpenMember, onOpenNewsletter }) {
  const [stats, setStats] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [adPlan, setAdPlan] = useState(null);
  const [digest, setDigest] = useState(null);
  useEffect(() => {
    client.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    client.get("/admin/approvals").then((r) => setInbox(r.data)).catch(() => {});
    client.get("/admin/ad-plan").then((r) => setAdPlan(r.data)).catch(() => {});
    client.get(`/admin/digest?origin=${encodeURIComponent(window.location.origin)}`).then((r) => setDigest(r.data)).catch(() => {});
  }, []);
  const currentEdition = adPlan?.editions?.find((e) => e.current);
  const slotsFree = currentEdition && adPlan ? Math.max(0, adPlan.cap - currentEdition.counts.total) : null;
  const cards = stats ? [
    { label: "Total Members", value: stats.members_total },
    { label: "Published", value: stats.members_published },
    { label: "In Review", value: stats.members_review },
    { label: "Active Subs", value: stats.active_subscriptions },
    { label: "Editorials", value: stats.articles_published },
    { label: "Member Features", value: stats.features_published },
    { label: "Social Posts", value: stats.social_posts },
    { label: "Ad Slots Free (This Edition)", value: slotsFree },
  ] : [];
  return (
    <div>
      <Header kicker="Dashboard" title="Overview" />
      <GrowthPanel />
      <StripeHealthPanel />
      <HealthScanPanel />
      {digest && (
        <div className="mx-6 sm:mx-10 mt-6 bg-ink text-cream p-6 sm:p-7 grain relative" data-testid="your-week-panel">
          <div className="relative z-10">
            <p className="overline text-gloss-gold mb-3">Your week · Edition of {digest.issue}</p>
            <div className="flex flex-wrap gap-x-8 gap-y-2 font-sans text-sm">
              <span data-testid="yw-signoff">{digest.needs_signoff > 0 ? <><b className="text-gloss-gold">{digest.needs_signoff}</b> profile{digest.needs_signoff === 1 ? "" : "s"} need your sign-off</> : "Nothing needs your sign-off"}</span>
              <span data-testid="yw-drip">{digest.dripping_this_edition} piece{digest.dripping_this_edition === 1 ? "" : "s"} scheduled to drip</span>
              <span data-testid="yw-ads">{digest.ad_slots_free} ad slot{digest.ad_slots_free === 1 ? "" : "s"} free</span>
              <span data-testid="yw-newmembers">{digest.new_members_this_week} new member{digest.new_members_this_week === 1 ? "" : "s"} this week</span>
              <button onClick={onOpenNewsletter} className="underline decoration-gloss-gold underline-offset-4 hover:text-gloss-gold transition-colors text-left" data-testid="yw-newsletter">Newsletter: {digest.newsletter_status === "sent" ? "sent" : "draft, awaiting you"} →</button>
              <span data-testid="yw-engine">Engine: {digest.auto_drip ? "automatic" : "manual (your switch is off)"}</span>
            </div>
            <p className="font-mono text-[11px] text-cream/45 mt-4" data-testid="yw-cost">Est. production cost per member: ~£{digest.est_cost_per_member_year}/year at current settings</p>
          </div>
        </div>
      )}
      <div className="p-6 sm:p-10 grid grid-cols-2 lg:grid-cols-4 gap-px bg-ink/15 border border-ink/15" data-testid="stats-grid">
        {cards.map((c) => (
          <div key={c.label} className="bg-cream p-7">
            <p className="font-serif text-5xl">{c.value ?? "—"}</p>
            <p className="overline text-ink/50 mt-2">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="px-6 sm:px-10 py-8" data-testid="approvals-inbox">
        <div className="flex items-center justify-between border-b border-ink/20 pb-4 mb-6">
          <h2 className="font-grotesk text-2xl font-medium flex items-center gap-3">
            Pending approvals
            {inbox && inbox.total > 0 && <span className="bg-gloss-gold text-ink font-mono text-sm px-2.5 py-0.5" data-testid="approvals-count">{inbox.total}</span>}
          </h2>
          <p className="overline text-ink/45 hidden sm:block">Nothing prints without you</p>
        </div>
        {!inbox && <p className="font-sans text-sm text-ink/50">Checking the desk…</p>}
        {inbox && inbox.total === 0 && (
          <p className="font-sans text-sm text-ink/55" data-testid="approvals-empty"><Check size={14} className="inline mr-1.5 text-gloss-gold" />All clear — nothing awaiting sign-off.</p>
        )}
        {inbox && inbox.profiles.length > 0 && (
          <InboxGroup title="Profiles awaiting your sign-off" note="Members submitted or updated their profile — review it, then Publish." testid="inbox-profiles">
            {inbox.profiles.map((p) => (
              <InboxRow key={p.id} testid={`inbox-profile-${p.id}`} main={p.business_name} sub={`${p.owner_name || "—"} · ${p.borough || "—"} · ${p.photos} photo${p.photos === 1 ? "" : "s"}`} onOpen={() => onOpenMember(p.id)} />
            ))}
          </InboxGroup>
        )}
        {inbox && inbox.interviews.length > 0 && (
          <InboxGroup title="Interview answers in" note="They've answered their 5 Questions — craft and publish the interview." testid="inbox-interviews">
            {inbox.interviews.map((p) => (
              <InboxRow key={p.id} testid={`inbox-interview-${p.id}`} main={p.business_name} sub={`Answers submitted ${String(p.submitted_at || "").slice(0, 10)}`} onOpen={() => onOpenMember(p.id)} />
            ))}
          </InboxGroup>
        )}
        {inbox && inbox.drafts.length > 0 && (
          <InboxGroup title="Drafted pieces awaiting a date" note="Written but not yet scheduled — set a drip date or publish now from the member's hub." testid="inbox-drafts">
            {inbox.drafts.map((d) => (
              <InboxRow key={d.id} testid={`inbox-draft-${d.id}`} main={d.title || "Untitled"} sub={{ member_feature: "Feature", spotlight: "Spotlight", edit: "The Edit", five_questions: "Interview", blog: "Members' Desk" }[d.type] || d.type} onOpen={() => onOpenMember(d.member_id)} />
            ))}
          </InboxGroup>
        )}
        {inbox && inbox.adverts.length > 0 && (
          <InboxGroup title="Adverts not yet booked" note="Written but not scheduled into an edition — book their slot in the member's hub." testid="inbox-adverts">
            {inbox.adverts.map((p) => (
              <InboxRow key={p.id} testid={`inbox-advert-${p.id}`} main={p.business_name} sub="Advert ready · no edition booked" onOpen={() => onOpenMember(p.id)} />
            ))}
          </InboxGroup>
        )}
      </div>
      <div className="px-6 sm:px-10 pb-10">
        <div className="bg-ink text-cream p-8 grain">
          <div className="relative z-10">
            <p className="overline text-gloss-gold mb-3">The Flywheel</p>
            <p className="font-serif text-2xl sm:text-3xl leading-snug max-w-3xl">
              Generate cutting-edge editorial → attract readers → members get seen → members get clients → members stay for life.
            </p>
            <p className="font-mono text-xs text-cream/50 mt-4">Every piece is AI-assisted then editor-controlled. You can write, edit, upload and publish manually at any point.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Production (Bird's-Eye) ---------------- */
